import { saveOperationLogApi } from '@/services/Applog/ApplogService';
import { alignSiteModuleSnapshots, summarizeBenefitPayloadForLog } from '@/utils/conferenceOpLogSnapshot';

export { pickAgendaSnapshot, buildAgendaSubmitParams } from '@/utils/conferenceOpLogSnapshot';

/**
 * 会议项目操作日志工具
 *
 * 记录会议项目管理、会议中嘉宾行程管理等操作日志
 *
 * 模板解析机制说明：
 * 1. operationDesc 中使用 {{variable_name}} 格式定义模板变量
 * 2. extra 字段提供变量的实际值，格式为 { variable_name: actualValue }
 * 3. 展示时通过 operationLogParser.js 解析模板，将变量替换为实际值
 *
 * Extra JSON 字段结构：
 * extra 字段中包含 expertInfo 对象，用于支持通过 getOperationLogListWithExtra 进行筛选查询
 */

/**
 * 获取当前管理员信息（从 localStorage）
 * @returns {Object|null} 管理员信息对象，包含 id, name, phone
 */
const getCurrentAdminInfo = () => {
    try {
        const infosStr = localStorage.getItem('infos');
        if (!infosStr) {
            console.warn('localStorage中未找到infos信息');
            return null;
        }

        const infosObj = JSON.parse(infosStr);
        if (!infosObj || !infosObj.data) {
            // 如果 data 字段不存在，尝试直接解析
            const directParse = typeof infosObj === 'object' && infosObj.id ? infosObj : null;
            if (directParse) {
                return {
                    id: directParse.id || null,
                    name: directParse.name || '',
                    phone: directParse.phone || '',
                };
            }
            console.warn('infos数据结构异常:', infosObj);
            return null;
        }

        const infos = JSON.parse(infosObj.data);
        return {
            id: infos.id || null,
            name: infos.name || '',
            phone: infos.phone || '',
        };
    } catch (error) {
        console.error('获取管理员信息失败:', error);
        return null;
    }
};

/**
 * 格式化日期时间为 HH:mm 格式
 * @param {string} datetime - 日期时间字符串
 * @returns {string} 格式化后的时间字符串
 */
const formatTime = datetime => {
    if (!datetime) return '';
    try {
        const date = new Date(datetime);
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${hours}:${minutes}`;
    } catch (error) {
        return datetime;
    }
};

/**
 * 格式化交通信息为文本描述
 * @param {Array} transports - 交通信息数组
 * @returns {string} 格式化后的交通信息文本
 */
const formatTransportInfo = transports => {
    if (!transports || transports.length === 0) {
        return '无';
    }
    return transports
        .map(transport => {
            const parts = [];
            if (transport.transport_type_text) {
                parts.push(transport.transport_type_text);
            }
            if (transport.transport_no) {
                parts.push(transport.transport_no);
            }
            if (transport.seat_type) {
                parts.push(transport.seat_type);
            }
            if (transport.departure_place && transport.arrival_place) {
                parts.push(`${transport.departure_place}→${transport.arrival_place}`);
            }
            if (transport.departure_datetime && transport.arrival_datetime) {
                const depTime = formatTime(transport.departure_datetime);
                const arrTime = formatTime(transport.arrival_datetime);
                parts.push(`${depTime}～${arrTime}`);
            }
            return parts.join(' ');
        })
        .join('，');
};

/**
 * 格式化用车信息为文本描述
 * @param {Object} carInfo - 用车信息对象
 * @returns {string} 格式化后的用车信息文本
 */
const formatCarInfo = carInfo => {
    if (!carInfo) {
        return '无';
    }
    const parts = [];
    if (carInfo.departure_place && carInfo.arrival_place) {
        parts.push(`${carInfo.departure_place}→${carInfo.arrival_place}`);
    }
    if (carInfo.departure_datetime) {
        const depTime = formatTime(carInfo.departure_datetime);
        parts.push(`${depTime}出发`);
    }
    return parts.join(' ');
};

/**
 * 格式化票信息为文本描述
 * @param {Object} transportInfo - 交通信息对象
 * @returns {string} 格式化后的票信息文本
 */
const formatTicketInfo = transportInfo => {
    if (!transportInfo || !transportInfo.transports || transportInfo.transports.length === 0) {
        return '无';
    }
    const directionText = transportInfo.direction === 'arrival' ? '来程交通' : '返程交通';
    const transport = transportInfo.transports[0];
    const parts = [directionText];
    if (transport.transport_type_text) {
        parts.push(transport.transport_type_text);
    }
    if (transport.transport_no) {
        parts.push(transport.transport_no);
    }
    if (transport.seat_type) {
        parts.push(transport.seat_type);
    }
    return parts.join(' ');
};

/**
 * 格式化酒店住宿信息为文本描述
 * @param {Object} hotelInfo - 酒店信息对象
 * @returns {string} 格式化后的酒店住宿信息文本
 */
const formatHotelInfo = hotelInfo => {
    if (!hotelInfo) {
        return '无';
    }
    const parts = [];
    if (hotelInfo.checkin_date && hotelInfo.checkout_date) {
        parts.push(`${hotelInfo.checkin_date}至${hotelInfo.checkout_date}`);
    }
    if (hotelInfo.hotel) {
        parts.push(hotelInfo.hotel);
    }
    if (hotelInfo.hotel_room_type) {
        parts.push(hotelInfo.hotel_room_type);
    }
    return parts.join(' ');
};

/**
 * 会议项目操作日志上报辅助函数
 * @param {Object} options - 日志参数
 * @param {string} options.entityType - 实体类型: 'conference'
 * @param {string|number} options.entityId - 实体ID（会议ID）
 * @param {string} options.operationType - 操作类型
 * @param {string} options.operationDesc - 操作描述（支持模板变量 {{variable}}）
 * @param {Object} options.expertInfo - 专家信息
 * @param {number} options.expertInfo.expertId - 专家ID
 * @param {string} options.expertInfo.expertName - 专家姓名
 * @param {Object} [options.adminInfo] - 管理员信息（可选，如果不提供则从 localStorage 获取）
 * @param {Object} [options.extra] - 其他扩展信息字段（可选，会自动合并到最终的 extra 中）
 * @param {string} [options.result] - 操作结果: 'success' 或 'failed'
 * @returns {Promise} 返回操作结果
 */
const logConferenceOperation = async options => {
    const { entityType, entityId, operationType, operationDesc, expertInfo, adminInfo = null, extra = null, result = 'success' } = options;

    // 获取管理员信息
    const finalAdminInfo = adminInfo || getCurrentAdminInfo();

    // 构建 extra 字段
    const finalExtra = {
        expertInfo: {
            expertId: expertInfo.expertId,
            expertName: expertInfo.expertName,
        },
        ...(extra || {}),
    };

    // 构建日志数据
    const logData = {
        entityType,
        entityId: String(entityId),
        module: 'guest_journey',
        portal: 'boss',
        operationType,
        operationDesc,
        extra: JSON.stringify(finalExtra),
        result,
        dataSnapshot: null,
        ...(finalAdminInfo
            ? {
                  adminId: finalAdminInfo.id,
                  adminPhone: finalAdminInfo.phone,
                  adminName: finalAdminInfo.name,
              }
            : {}),
    };

    try {
        const response = await saveOperationLogApi(logData);
        console.log('会议项目操作日志上报成功', logData);
        return response;
    } catch (error) {
        console.error('会议项目操作日志上报失败', error, logData);
        throw error;
    }
};

/**
 * 变更来程交通日志
 * @param {Object} options - 日志参数
 * @param {number} options.conferenceId - 会议ID
 * @param {number} options.expertId - 专家ID
 * @param {string} options.expertName - 专家姓名
 * @param {Array} options.transports - 交通信息数组
 * @param {string} [options.reason] - 变更理由
 * @param {Object} [options.adminInfo] - 管理员信息（可选）
 * @param {boolean} [options.isSuccess] - 是否成功（可选，默认 true）
 * @returns {Promise} 返回操作结果
 *
 * @example
 * await logChangeArrivalTransport({
 *   conferenceId: 1070,
 *   expertId: 345435,
 *   expertName: '张三',
 *   transports: [{
 *     transport_type: 1,
 *     transport_type_text: '飞机',
 *     transport_no: 'CA76837',
 *     seat_type: '经济舱',
 *     departure_datetime: '2024-11-12 10:00:00',
 *     arrival_datetime: '2024-11-12 13:45:00',
 *     departure_place: '首都',
 *     arrival_place: '美兰机场T2'
 *   }],
 *   reason: '变更理由'
 * });
 */
export const logChangeArrivalTransport = async options => {
    const { conferenceId, expertId, expertName, transports, reason = '', adminInfo = null, isSuccess = true } = options;

    const transportInfo = formatTransportInfo(transports);

    return logConferenceOperation({
        entityType: 'conference',
        entityId: conferenceId,
        operationType: 'change_arrival_transport',
        operationDesc: '变更来程交通（健康界平台操作；嘉宾姓名：{{expertInfo.expertName}}；交通信息：{{transportInfo}}）',
        expertInfo: {
            expertId,
            expertName,
        },
        adminInfo,
        extra: {
            transports, // 原始交通信息数组，用于格式化 transportInfo
            reason, // 变更理由
        },
        result: isSuccess ? 'success' : 'failed',
    });
};

/**
 * 变更来程用车日志
 * @param {Object} options - 日志参数
 * @param {number} options.conferenceId - 会议ID
 * @param {number} options.expertId - 专家ID
 * @param {string} options.expertName - 专家姓名
 * @param {Object} options.carInfo - 用车信息
 * @param {string} [options.reason] - 变更理由（可选）
 * @param {Object} [options.adminInfo] - 管理员信息（可选）
 * @param {boolean} [options.isSuccess] - 是否成功（可选，默认 true）
 * @returns {Promise} 返回操作结果
 *
 * @example
 * await logChangeArrivalCar({
 *   conferenceId: 1070,
 *   expertId: 345435,
 *   expertName: '张三',
 *   carInfo: {
 *     car_type_demand: '六座商务',
 *     departure_datetime: '2024-11-23 09:30',
 *     departure_place: '海口机场',
 *     arrival_place: '某某万豪酒店',
 *     car_type: 'gl8',
 *     car_no: '琼A38927',
 *     driver_name: '老默',
 *     driver_phone: '12345678901'
 *   },
 *   reason: '变更理由'
 * });
 */
export const logChangeArrivalCar = async options => {
    const { conferenceId, expertId, expertName, carInfo, reason = '', adminInfo = null, isSuccess = true } = options;

    const carInfoText = formatCarInfo(carInfo);

    return logConferenceOperation({
        entityType: 'conference',
        entityId: conferenceId,
        operationType: 'change_arrival_car',
        operationDesc: '变更来程用车（健康界平台操作；嘉宾姓名：{{expertInfo.expertName}}；用车信息：{{carInfo}}）',
        expertInfo: {
            expertId,
            expertName,
        },
        adminInfo,
        extra: {
            carInfo: {
                ...carInfo,
                // 格式化后的文本用于模板变量替换
                formatted: carInfoText,
            },
            reason, // 变更理由
        },
        result: isSuccess ? 'success' : 'failed',
    });
};

/**
 * 变更返程交通日志
 * @param {Object} options - 日志参数
 * @param {number} options.conferenceId - 会议ID
 * @param {number} options.expertId - 专家ID
 * @param {string} options.expertName - 专家姓名
 * @param {Array} options.transports - 交通信息数组
 * @param {string} [options.reason] - 变更理由（可选）
 * @param {Object} [options.adminInfo] - 管理员信息（可选）
 * @param {boolean} [options.isSuccess] - 是否成功（可选，默认 true）
 * @returns {Promise} 返回操作结果
 *
 * @example
 * await logChangeReturnTransport({
 *   conferenceId: 1070,
 *   expertId: 345435,
 *   expertName: '张三',
 *   transports: [{
 *     transport_type: 1,
 *     transport_type_text: '飞机',
 *     transport_no: 'CA76837',
 *     seat_type: '经济舱'
 *   }],
 *   reason: '变更理由'
 * });
 */
export const logChangeReturnTransport = async options => {
    const { conferenceId, expertId, expertName, transports, reason = '', adminInfo = null, isSuccess = true } = options;

    const transportInfo = formatTransportInfo(transports);

    return logConferenceOperation({
        entityType: 'conference',
        entityId: conferenceId,
        operationType: 'change_return_transport',
        operationDesc: '变更返程交通（健康界平台操作；嘉宾姓名：{{expertInfo.expertName}}；交通信息：{{transportInfo}}）',
        expertInfo: {
            expertId,
            expertName,
        },
        adminInfo,
        extra: {
            transports, // 原始交通信息数组，用于格式化 transportInfo
            reason, // 变更理由
        },
        result: isSuccess ? 'success' : 'failed',
    });
};

/**
 * 变更返程用车日志
 * @param {Object} options - 日志参数
 * @param {number} options.conferenceId - 会议ID
 * @param {number} options.expertId - 专家ID
 * @param {string} options.expertName - 专家姓名
 * @param {Object} options.carInfo - 用车信息
 * @param {string} [options.reason] - 变更理由（可选）
 * @param {Object} [options.adminInfo] - 管理员信息（可选）
 * @param {boolean} [options.isSuccess] - 是否成功（可选，默认 true）
 * @returns {Promise} 返回操作结果
 *
 * @example
 * await logChangeReturnCar({
 *   conferenceId: 1070,
 *   expertId: 345435,
 *   expertName: '张三',
 *   carInfo: {
 *     departure_datetime: '2024-11-23 09:30',
 *     departure_place: '海口机场',
 *     arrival_place: '某某万豪酒店'
 *   },
 *   reason: '变更理由'
 * });
 */
export const logChangeReturnCar = async options => {
    const { conferenceId, expertId, expertName, carInfo, reason = '', adminInfo = null, isSuccess = true } = options;

    const carInfoText = formatCarInfo(carInfo);

    return logConferenceOperation({
        entityType: 'conference',
        entityId: conferenceId,
        operationType: 'change_return_car',
        operationDesc: '变更返程用车（健康界平台操作；嘉宾姓名：{{expertInfo.expertName}}；用车信息：{{carInfo}}）',
        expertInfo: {
            expertId,
            expertName,
        },
        adminInfo,
        extra: {
            carInfo: {
                ...carInfo,
                // 格式化后的文本用于模板变量替换
                formatted: carInfoText,
            },
            reason, // 变更理由
        },
        result: isSuccess ? 'success' : 'failed',
    });
};

/**
 * 变更酒店住宿日志
 * @param {Object} options - 日志参数
 * @param {number} options.conferenceId - 会议ID
 * @param {number} options.expertId - 专家ID
 * @param {string} options.expertName - 专家姓名
 * @param {Object} options.hotelInfo - 酒店信息
 * @param {string} [options.reason] - 变更理由（可选）
 * @param {Object} [options.adminInfo] - 管理员信息（可选）
 * @param {boolean} [options.isSuccess] - 是否成功（可选，默认 true）
 * @returns {Promise} 返回操作结果
 *
 * @example
 * await logChangeHotelInfo({
 *   conferenceId: 1070,
 *   expertId: 345435,
 *   expertName: '张三',
 *   hotelInfo: {
 *     checkin_date: '2024-11-22',
 *     checkout_date: '2024-11-24',
 *     hotel_id: 1111,
 *     hotel: '喜来登',
 *     hotel_room_type_id: 222,
 *     hotel_room_type: '大床房'
 *   },
 *   reason: '变更理由'
 * });
 */
export const logChangeHotelInfo = async options => {
    const { conferenceId, expertId, expertName, hotelInfo, reason = '', adminInfo = null, isSuccess = true } = options;

    const hotelInfoText = formatHotelInfo(hotelInfo);

    return logConferenceOperation({
        entityType: 'conference',
        entityId: conferenceId,
        operationType: 'change_hotelinfo',
        operationDesc: '变更酒店住宿（健康界平台操作；嘉宾姓名：{{expertInfo.expertName}}；酒店住宿：{{hotelInfo}}）',
        expertInfo: {
            expertId,
            expertName,
        },
        adminInfo,
        extra: {
            hotelInfo: {
                ...hotelInfo,
                // 格式化后的文本用于模板变量替换
                formatted: hotelInfoText,
            },
            reason, // 变更理由
        },
        result: isSuccess ? 'success' : 'failed',
    });
};

/**
 * 出票日志
 * @param {Object} options - 日志参数
 * @param {number} options.conferenceId - 会议ID
 * @param {number} options.expertId - 专家ID
 * @param {string} options.expertName - 专家姓名
 * @param {Object} options.transportInfo - 交通信息
 * @param {string} options.transportInfo.direction - 方向: 'arrival' 或 'return'
 * @param {Array} options.transportInfo.transports - 交通信息数组
 * @param {string} [options.reason] - 变更理由（可选）
 * @param {Object} [options.adminInfo] - 管理员信息（可选）
 * @param {boolean} [options.isSuccess] - 是否成功（可选，默认 true）
 * @returns {Promise} 返回操作结果
 *
 * @example
 * await logIssueTicket({
 *   conferenceId: 1070,
 *   expertId: 345435,
 *   expertName: '张三',
 *   transportInfo: {
 *     direction: 'arrival',
 *     transports: [{
 *       transport_type: 1,
 *       transport_type_text: '飞机',
 *       transport_no: 'CA76837',
 *       seat_type: '经济舱'
 *     }]
 *   },
 * });
 */
export const logIssueTicket = async options => {
    const { conferenceId, expertId, expertName, transportInfo, adminInfo = null, isSuccess = true } = options;

    return logConferenceOperation({
        entityType: 'conference',
        entityId: conferenceId,
        operationType: 'issue_ticket',
        operationDesc: '出票（健康界平台操作；嘉宾姓名：{{expertInfo.expertName}}；票信息：{{ticketInfo}}）',
        expertInfo: {
            expertId,
            expertName,
        },
        adminInfo,
        extra: {
            transportInfo, // 包含 direction 和 transports，用于格式化 ticketInfo
        },
        result: isSuccess ? 'success' : 'failed',
    });
};

/**
 * 标记为未出票日志
 * @param {Object} options - 日志参数
 * @param {number} options.conferenceId - 会议ID
 * @param {number} options.expertId - 专家ID
 * @param {string} options.expertName - 专家姓名
 * @param {Object} options.transportInfo - 交通信息
 * @param {string} options.transportInfo.direction - 方向: 'arrival' 或 'return'
 * @param {Array} options.transportInfo.transports - 交通信息数组
 * @param {Object} [options.adminInfo] - 管理员信息（可选）
 * @param {boolean} [options.isSuccess] - 是否成功（可选，默认 true）
 * @returns {Promise} 返回操作结果
 *
 * @example
 * await logCancelTicket({
 *   conferenceId: 1070,
 *   expertId: 345435,
 *   expertName: '张三',
 *   transportInfo: {
 *     direction: 'arrival',
 *     transports: [{
 *       transport_type: 1,
 *       transport_type_text: '飞机',
 *       transport_no: 'CA76837',
 *       seat_type: '经济舱'
 *     }]
 *   }
 * });
 */
export const logCancelTicket = async options => {
    const { conferenceId, expertId, expertName, transportInfo, adminInfo = null, isSuccess = true } = options;

    return logConferenceOperation({
        entityType: 'conference',
        entityId: conferenceId,
        operationType: 'cancel_ticket',
        operationDesc: '标记为未出票（健康界平台操作；嘉宾姓名：{{expertInfo.expertName}}；票信息：{{ticketInfo}}）',
        expertInfo: {
            expertId,
            expertName,
        },
        adminInfo,
        extra: {
            transportInfo, // 包含 direction 和 transports，用于格式化 ticketInfo
        },
        result: isSuccess ? 'success' : 'failed',
    });
};

// ── 会议官网 / 日程 / 机构权益（module 见 docs/applog/操作日志定义__医会议-*.md）──

const logBossApplogOperation = async ({ module, entityType, entityId, operationType, operationDesc, extra = null, result = 'success', dataSnapshot = null }) => {
    const adminInfo = getCurrentAdminInfo();
    const logData = {
        entityType,
        entityId: entityId != null && entityId !== '' ? String(entityId) : null,
        module,
        portal: 'boss',
        operationType,
        operationDesc,
        extra: extra ? JSON.stringify(extra) : null,
        result,
        dataSnapshot: dataSnapshot ? JSON.stringify(dataSnapshot) : null,
        ...(adminInfo
            ? {
                  adminId: adminInfo.id,
                  adminPhone: adminInfo.phone,
                  adminName: adminInfo.name,
              }
            : {}),
    };

    try {
        const response = await saveOperationLogApi(logData);
        console.log('医会议业务操作日志上报成功', logData);
        return response;
    } catch (error) {
        console.error('医会议业务操作日志上报失败', error, logData);
        throw error;
    }
};

export const logSiteModuleConfigSave = async ({ entityId, moduleLabel, moduleType, conferenceId, before, after, isSuccess = true, errorMessage = null }) => {
    const extra = {
        moduleLabel: moduleLabel || '',
        moduleType,
        conferenceId,
        ...(isSuccess ? {} : { error_message: errorMessage || '保存失败' }),
    };
    return logBossApplogOperation({
        module: 'conference',
        entityType: 'site_module',
        entityId: isSuccess || entityId ? entityId : null,
        operationType: 'update',
        operationDesc: `编辑会议官网模块（{{moduleLabel}}）`,
        extra,
        result: isSuccess ? 'success' : 'failed',
        dataSnapshot: isSuccess ? alignSiteModuleSnapshots(before, after, conferenceId) : null,
    });
};

export const logAgendaCreate = async ({ hallId, hallName, agendaId, agendaTitle, afterSnapshot, isSuccess = true, errorMessage = null }) => {
    return logBossApplogOperation({
        module: 'conference',
        entityType: 'conference_hall',
        entityId: hallId,
        operationType: 'create_agenda',
        operationDesc: '新增日程（{{agendaTitle}}）',
        extra: {
            hallId,
            hallName,
            agendaId,
            agendaTitle: agendaTitle || `ID:${agendaId}`,
            ...(isSuccess ? {} : { error_message: errorMessage }),
        },
        result: isSuccess ? 'success' : 'failed',
        dataSnapshot: isSuccess ? { before: null, after: afterSnapshot } : null,
    });
};

export const logAgendaUpdate = async ({ hallId, hallName, agendaId, agendaTitle, beforeSnapshot, afterSnapshot, isSuccess = true, errorMessage = null }) => {
    return logBossApplogOperation({
        module: 'conference',
        entityType: 'conference_hall',
        entityId: hallId,
        operationType: 'update_agenda',
        operationDesc: '编辑日程（{{agendaTitle}}）',
        extra: {
            hallId,
            hallName,
            agendaId,
            agendaTitle: agendaTitle || `ID:${agendaId}`,
            ...(isSuccess ? {} : { error_message: errorMessage }),
        },
        result: isSuccess ? 'success' : 'failed',
        dataSnapshot: isSuccess ? { before: beforeSnapshot, after: afterSnapshot } : null,
    });
};

export const logAgendaReorder = async ({ hallId, hallName, beforeIds, afterIds, isSuccess = true, errorMessage = null }) => {
    return logBossApplogOperation({
        module: 'conference',
        entityType: 'conference_hall',
        entityId: hallId,
        operationType: 'reorder_agenda',
        operationDesc: '调整日程排序（场次：{{hallName}}）',
        extra: {
            hallId,
            hallName,
            ...(isSuccess ? {} : { error_message: errorMessage }),
        },
        result: isSuccess ? 'success' : 'failed',
        dataSnapshot: isSuccess ? { before: beforeIds, after: afterIds } : null,
    });
};

export const logOrgBenefitCreate = async ({ sponsorId, conferenceId, afterPayload, isSuccess = true, errorMessage = null }) => {
    return logBossApplogOperation({
        module: 'conference',
        entityType: 'conference_sponsor',
        entityId: isSuccess ? sponsorId : sponsorId || null,
        operationType: 'create_benefit',
        operationDesc: '新增机构权益',
        extra: isSuccess ? { conferenceId } : { conferenceId, error_message: errorMessage },
        result: isSuccess ? 'success' : 'failed',
        dataSnapshot: isSuccess ? { before: null, after: summarizeBenefitPayloadForLog(afterPayload) } : null,
    });
};

export const logOrgBenefitUpdate = async ({ sponsorId, conferenceId, beforePayload, afterPayload, isSuccess = true, errorMessage = null }) => {
    return logBossApplogOperation({
        module: 'conference',
        entityType: 'conference_sponsor',
        entityId: sponsorId || null,
        operationType: 'update_benefit',
        operationDesc: '编辑机构权益',
        extra: isSuccess ? { conferenceId } : { conferenceId, error_message: errorMessage },
        result: isSuccess ? 'success' : 'failed',
        dataSnapshot: isSuccess ? { before: summarizeBenefitPayloadForLog(beforePayload), after: summarizeBenefitPayloadForLog(afterPayload) } : null,
    });
};

export const logOrgBenefitRealize = async ({ sponsorId, orgName, changeSnapshot, isSuccess = true, errorMessage = null }) => {
    return logBossApplogOperation({
        module: 'conference',
        entityType: 'conference_sponsor',
        entityId: sponsorId,
        operationType: 'realize_benefit',
        operationDesc: '权益兑现（机构：{{orgName}}）',
        extra: {
            orgName: orgName || '',
            ...(isSuccess ? {} : { error_message: errorMessage }),
        },
        result: isSuccess ? 'success' : 'failed',
        dataSnapshot: isSuccess ? changeSnapshot || null : null,
    });
};
